import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PRACTICE_SET_SIZES, type ExamCode } from '@titrate/shared';
import { api, ApiRequestError } from '../lib/api';
import { useEntitlements, useExams, useMe } from '../lib/queries';
import { ErrorNotice, Spinner } from '../components/ui';

interface TagRow { name: string; type: string; weightPct: number | null }

export function Practice() {
  const [params] = useSearchParams();
  const { data: me } = useMe();
  const { data: exams, isLoading } = useExams();
  const { data: ent } = useEntitlements();
  const navigate = useNavigate();

  const target = me?.user?.examTarget ?? 'CAOP';
  const available: ExamCode[] = target === 'BOTH' ? ['CAOP', 'OPRA'] : [target as ExamCode];

  const [examCode, setExamCode] = useState<ExamCode>((params.get('exam') as ExamCode) ?? available[0] ?? 'CAOP');
  const [domainCode, setDomainCode] = useState(params.get('domain') ?? '');
  const [tagName, setTagName] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [count, setCount] = useState<number>(20);
  const [timed, setTimed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: tags } = useQuery({
    queryKey: ['tags', examCode],
    queryFn: () => api.get<{ tags: TagRow[] }>(`/api/exams/${examCode}/tags`),
    staleTime: 5 * 60_000,
  });

  const exam = exams?.exams.find((e) => e.code === examCode);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const { sessionId } = await api.post<{ sessionId: string }>('/api/sessions/practice', {
        examCode,
        count,
        timed,
        ...(domainCode ? { domainCode } : {}),
        ...(tagName ? { tagName } : {}),
        ...(difficulty ? { difficulty } : {}),
      });
      navigate(`/exam/${sessionId}`);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not start that practice set.');
      setBusy(false);
    }
  };

  if (isLoading) return <Spinner />;

  const remaining = ent?.entitlements?.practiceRemaining ?? null;
  // What the user will actually be served, once the daily cap is applied.
  const servedCount = remaining === null ? count : Math.min(count, remaining);

  const therapeuticTags = tags?.tags.filter((t) => t.type === 'therapeutic_area') ?? [];
  const cognitiveTags = tags?.tags.filter((t) => t.type === 'cognitive_level') ?? [];

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-3xl font-bold tracking-tight text-ink">Practice</h1>
      <p className="mt-2 text-muted">
        Short, targeted sets drawn from the domains and topics you choose.
      </p>

      <div className="card mt-6 space-y-6 p-4 sm:p-6">
        {error ? <ErrorNotice message={error} /> : null}

        {available.length > 1 ? (
          <div>
            <span className="label">Exam</span>
            <div className="flex flex-wrap gap-2">
              {available.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => { setExamCode(code); setDomainCode(''); setTagName(''); }}
                  className={examCode === code ? 'btn-primary' : 'btn-secondary'}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div>
          <label className="label" htmlFor="domain">Content domain</label>
          <select id="domain" className="input" value={domainCode} onChange={(e) => setDomainCode(e.target.value)}>
            <option value="">All domains (weighted like the real paper)</option>
            {exam?.domains.map((d) => (
              <option key={d.code} value={d.code}>
                {d.code} — {d.name} ({d.weightPct}%)
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="tag">Topic</label>
          <select id="tag" className="input" value={tagName} onChange={(e) => setTagName(e.target.value)}>
            <option value="">Any topic</option>
            {therapeuticTags.length > 0 ? (
              <optgroup label="Therapeutic area">
                {therapeuticTags.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
              </optgroup>
            ) : null}
            {cognitiveTags.length > 0 ? (
              <optgroup label="Cognitive level">
                {cognitiveTags.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
              </optgroup>
            ) : null}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="difficulty">Difficulty</label>
          <select id="difficulty" className="input" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
            <option value="">Any difficulty</option>
            <option value="easy">Easy</option>
            <option value="moderate">Moderate</option>
            <option value="hard">Hard</option>
          </select>
        </div>

        <div>
          <span className="label">Set size</span>
          {/*
            A grid rather than a flex row: "10 questions / 20 questions /
            40 questions" side by side overflows every phone width, and the
            noun is already carried by the label and the submit button.
          */}
          <div className="grid grid-cols-3 gap-2">
            {PRACTICE_SET_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                aria-label={`${size} questions`}
                onClick={() => setCount(size)}
                className={count === size ? 'btn-primary' : 'btn-secondary'}
              >
                {size}
                <span className="hidden sm:inline"> questions</span>
              </button>
            ))}
          </div>
        </div>

        <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line bg-raised p-3.5">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-brand-700" checked={timed} onChange={(e) => setTimed(e.target.checked)} />
          <span className="text-sm text-muted">
            Timed
            <span className="mt-0.5 block text-xs text-muted">
              {exam ? `${exam.benchmarkPaceSec}s per question — the real ${exam.code} pace.` : 'Uses the real exam pace.'}
            </span>
          </span>
        </label>

        {/*
          A free plan rations practice by the day. Showing what is left before
          the set is built means a trimmed set is never a surprise — the API
          serves as many as remain rather than refusing outright.
        */}
        {remaining !== null ? (
          <div className="rounded-lg border border-line bg-raised p-3.5 text-sm">
            {remaining > 0 ? (
              <p className="text-muted">
                <span className="font-semibold text-ink">{remaining}</span> of your{' '}
                {ent?.entitlements?.practicePerDay} daily practice questions left.
                {count > remaining ? ` This set will be ${remaining} questions.` : ''}{' '}
                <Link to="/pricing" className="font-medium text-brand-700 underline underline-offset-2 dark:text-brand-300">
                  Upgrade for unlimited
                </Link>
              </p>
            ) : (
              <p className="text-muted">
                You have used today&apos;s {ent?.entitlements?.practicePerDay} practice questions. They
                free up 24 hours after each one, or{' '}
                <Link to="/pricing" className="font-medium text-brand-700 underline underline-offset-2 dark:text-brand-300">
                  upgrade for unlimited practice
                </Link>
                .
              </p>
            )}
          </div>
        ) : null}

        <button
          type="button"
          className="btn-primary w-full py-3"
          onClick={() => void start()}
          disabled={busy || remaining === 0}
        >
          {busy
            ? 'Building your set…'
            : remaining === 0
              ? 'Daily practice used up'
              : `Start ${servedCount}-question set`}
        </button>
      </div>
    </div>
  );
}
